import { createContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';

interface Profile {
  id: string;
  email: string;
  membership_status: 'ACTIVE' | 'INACTIVE';
  membership_tier?: string;
  billing_cycle?: string;
  created_at?: string;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  isActive: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  activateAdminBypass: (code: string) => boolean;
  deactivateAdminBypass: () => void;
  isBypassActive: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  session: null,
  isActive: false,
  loading: true,
  signOut: async () => {},
  refreshProfile: async () => {},
  activateAdminBypass: () => false,
  deactivateAdminBypass: () => {},
  isBypassActive: false,
});

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isBypassActive, setIsBypassActive] = useState<boolean>(
    () => localStorage.getItem('admin_bypass') === 'MATHMASTER_ADMIN_2025'
  );

  const BYPASS_CODE = 'MATHMASTER_ADMIN_2025';

  const activateAdminBypass = (code: string): boolean => {
    if (code === BYPASS_CODE) {
      localStorage.setItem('admin_bypass', BYPASS_CODE);
      setIsBypassActive(true);
      return true;
    }
    return false;
  };

  const deactivateAdminBypass = () => {
    localStorage.removeItem('admin_bypass');
    setIsBypassActive(false);
  };

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }

      return data;
    } catch (err) {
      console.error('Profile fetch error:', err);
      return null;
    }
  };

  const refreshProfile = async () => {
    if (user) {
      const profileData = await fetchProfile(user.id);
      setProfile(profileData);
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        fetchProfile(session.user.id).then((profileData) => {
          setProfile(profileData);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        const profileData = await fetchProfile(session.user.id);
        setProfile(profileData);
      } else {
        setProfile(null);
      }

      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setSession(null);
    deactivateAdminBypass();
  };

  const isActive = isBypassActive || profile?.membership_status === 'ACTIVE';

  const value: AuthContextType = {
    user,
    profile,
    session,
    isActive,
    loading,
    signOut,
    refreshProfile,
    activateAdminBypass,
    deactivateAdminBypass,
    isBypassActive,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}